import React, { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { startLevelMeter } from "@/lib/orbLevel";

/* Speech playback for assistant replies (tts-1 via Universal Key).
   Orb: thinking while fetching, speaking only from the real 'playing' event, idle on ended/error/stop.
   speak(id, text, { wait: true }) resolves only after playback really ended (for voice turn-taking). */
export const useSpeech = ({ setOrbState, fallbackState = "idle" }) => {
  const [status, setStatus] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [lastCost, setLastCost] = useState(null);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const meterStopRef = useRef(null);
  const statusRef = useRef(null);
  const doneRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get("/tts/status"); statusRef.current = r.data; setStatus(r.data); return r.data; }
    catch (e) { return null; }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { statusRef.current = status; }, [status]);

  const release = useCallback(() => {
    meterStopRef.current?.(); meterStopRef.current = null;
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
    audioRef.current = null;
    setPlayingId(null);
    setOrbState?.(fallbackState);
    const d = doneRef.current; doneRef.current = null; d?.(true);
  }, [setOrbState, fallbackState]);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) { a.onended = null; a.onerror = null; a.onpause = null; a.pause(); }
    release();
  }, [release]);

  useEffect(() => () => { const a = audioRef.current; if (a) a.pause(); meterStopRef.current?.(); if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  // Play an audio URL. wait=true → resolves after ended/stopped; revoke=false keeps cached URLs alive.
  const playUrl = useCallback((id, url, { wait = false, revoke = true, onPlaying } = {}) => new Promise((resolve) => {
    stop();
    const a = new Audio(url);
    audioRef.current = a; urlRef.current = revoke ? url : null;
    if (wait) doneRef.current = resolve;
    const failed = (msg) => { doneRef.current = null; toast.error(msg); release(); resolve(false); };
    a.onplaying = () => {
      onPlaying?.();
      setPlayingId(id); setOrbState?.("speaking");
      if (!meterStopRef.current) meterStopRef.current = startLevelMeter(a);
      if (!wait) resolve(true);
    };
    a.onpause = () => { if (!a.ended) release(); };
    a.onended = release;
    a.onerror = () => failed(`Ses dosyası oynatılamadı (${a.error?.code ? `MediaError ${a.error.code}` : "biçim/akış hatası"})`);
    a.play().catch((e) => failed(e?.name === "NotAllowedError"
      ? "Tarayıcı ses çalmayı engelledi (NotAllowedError) — sayfaya bir kez tıklayıp tekrar deneyin."
      : `Ses oynatılamadı (${e?.name || "oynatma hatası"})`));
  }), [stop, release, setOrbState]);

  const fetchSpeech = useCallback(async (text) => {
    const st = statusRef.current || await loadStatus();
    if (!st?.available) { toast.error(st?.message || "Sesli yanıt kullanılamıyor"); return null; }
    const r = await api.post("/tts/speak", { text }, { responseType: "blob" });
    const cost = r.headers["x-tts-cost-usd"]; const chars = r.headers["x-tts-chars"];
    setLastCost({ chars, usd: cost && cost !== "None" ? Number(cost) : null, voice: r.headers["x-tts-voice"] });
    return URL.createObjectURL(r.data);
  }, [loadStatus]);

  const speak = useCallback(async (id, text, opts = {}) => {
    if (!text?.trim()) return false;
    stop();
    setLoadingId(id); setOrbState?.("thinking");
    try {
      const url = await fetchSpeech(text);
      setLoadingId(null);
      if (!url) { release(); return false; }
      return await playUrl(id, url, opts);
    } catch (e) {
      const d = e?.response?.data;
      const code = e?.response?.status;
      const msg = d instanceof Blob ? await d.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } }) : d?.detail;
      toast.error(msg || (code ? `Sesli yanıt üretilemedi (HTTP ${code})` : "Sesli yanıt üretilemedi — ağ hatası"));
      release();
      return false;
    } finally { setLoadingId(null); }
  }, [stop, release, setOrbState, fetchSpeech, playUrl]);

  return { status, setStatus, speak, stop, playUrl, fetchSpeech, fetchStatus: loadStatus, playingId, loadingId, lastCost };
};

export const SpeakButton = ({ id, text, speech }) => {
  const { speak, stop, playingId, loadingId, status } = speech;
  if (!status) return null;
  if (playingId === id) {
    return (
      <button data-testid={`tts-stop-btn-${id}`} onClick={stop}
        className="inline-flex items-center gap-1 text-[0.68rem] px-2 py-0.5 rounded-md border border-red-400/40 text-red-200 bg-red-500/15 hover:bg-red-500/25">
        <Square size={11} /> Durdur
      </button>
    );
  }
  return (
    <button data-testid={`tts-speak-btn-${id}`} onClick={() => speak(id, text)} disabled={!status.available || loadingId === id}
      title={status.available ? `Sesli oku (${status.model}, Emergent kredisi)` : status.message}
      className="inline-flex items-center gap-1 text-[0.68rem] px-2 py-0.5 rounded-md border border-cyan-400/30 text-cyan-200 bg-cyan-500/10 hover:bg-cyan-500/20 disabled:opacity-40">
      {loadingId === id ? <Loader2 size={11} className="animate-spin" /> : <Volume2 size={11} />} {loadingId === id ? "Üretiliyor…" : "Sesli oku"}
    </button>
  );
};
